import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Settings as SettingsIcon, Save, Building2 } from "lucide-react";

export default function SettingsPage() {
  const { tenant, loadTenant, entities, loadEntities } = useApp();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("tenant");

  useEffect(() => {
    if (tenant) setForm(tenant);
  }, [tenant]);

  const saveTenant = async () => {
    setSaving(true);
    try {
      await api.put("/settings/tenant", form);
      await loadTenant();
      alert("Settings saved");
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const tabs = [
    { id: "tenant", label: "Group Profile" },
    { id: "entities", label: "Entities" },
    { id: "security", label: "Security" },
    { id: "backup", label: "Backup & Export" },
  ];

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
          <SettingsIcon size={24} className="inline mr-2 text-[#C8A961]" />
          Settings
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">Tenant administration and configuration</p>
      </div>

      <div className="flex gap-2 border-b border-[#E8E2D5]">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab.id ? "border-[#C8A961] text-[#0B1020]" : "border-transparent text-[#6B7280] hover:text-[#1A1A1A]"}`}
            data-testid={`settings-tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "tenant" && (
        <div className="stratum-card p-6 max-w-2xl">
          <h3 className="font-display text-lg font-semibold mb-4">Group Profile</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Group Name</label>
                <input className="stratum-input w-full" value={form.name || ""} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="settings-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Legal Name</label>
                <input className="stratum-input w-full" value={form.legal_name || ""} onChange={(e) => setForm({...form, legal_name: e.target.value})} data-testid="settings-legal-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Registration Number</label>
                <input className="stratum-input w-full font-mono" value={form.registration_number || ""} onChange={(e) => setForm({...form, registration_number: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">VAT Number</label>
                <input className="stratum-input w-full font-mono" value={form.vat_number || ""} onChange={(e) => setForm({...form, vat_number: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Financial Year End</label>
                <input type="date" className="stratum-input w-full" value={form.financial_year_end || ""} onChange={(e) => setForm({...form, financial_year_end: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Auditor</label>
                <input className="stratum-input w-full" value={form.auditor_name || ""} onChange={(e) => setForm({...form, auditor_name: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Currency</label>
              <input className="stratum-input w-32 font-mono" value={form.functional_currency || "ZAR"} onChange={(e) => setForm({...form, functional_currency: e.target.value})} />
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={saveTenant} disabled={saving} className="stratum-btn-primary flex items-center gap-2" data-testid="save-settings-btn">
                <Save size={16} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "entities" && (
        <div className="space-y-4">
          <button onClick={async () => {
            const name = prompt("Entity name:");
            const code = prompt("Entity code (short):");
            if (!name || !code) return;
            try {
              await api.post("/entities", { name, code, legal_name: name, role: "subsidiary" });
              await loadEntities();
              alert("Entity created!");
            } catch (e) { alert(e.response?.data?.detail || "Error"); }
          }} className="stratum-btn-primary flex items-center gap-2" data-testid="add-entity-btn">
            <Building2 size={16} /> Add Entity
          </button>
          {entities.map(ent => (
            <div key={ent.id} className="stratum-card p-5 flex items-center justify-between" data-testid={`entity-card-${ent.code}`}>
              <div className="flex items-center gap-3">
                <Building2 size={20} className="text-[#C8A961]" />
                <div>
                  <h4 className="font-medium text-[#0B1020]">{ent.name}</h4>
                  <p className="text-xs text-[#6B7280]">{ent.code} - {ent.legal_name} ({ent.role})</p>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${ent.is_active ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#991B1B]/10 text-[#991B1B]"}`}>
                {ent.is_active ? "Active" : "Inactive"}
              </span>
            </div>
          ))}
          {entities.length === 0 && (
            <div className="stratum-card p-8 text-center text-[#6B7280]">
              <Building2 size={32} className="mx-auto text-[#C8A961] mb-3" />
              <p>No entities yet. Add your first company entity to get started.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "security" && (
        <div className="stratum-card p-6 max-w-2xl">
          <h3 className="font-display text-lg font-semibold mb-4">Security Settings</h3>
          <div className="space-y-4 text-sm text-[#6B7280]">
            <div className="p-4 rounded-lg bg-[#FAF8F4]">
              <h4 className="font-medium text-[#0B1020] mb-1">Authentication</h4>
              <p>JWT-based authentication with httpOnly cookies. Session expires after 24 hours.</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4]">
              <h4 className="font-medium text-[#0B1020] mb-1">Role-Based Access Control</h4>
              <p>8 roles with granular permissions. Enforced on every API route.</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4]">
              <h4 className="font-medium text-[#0B1020] mb-1">Audit Trail</h4>
              <p>Every mutation is logged with before/after state, user, timestamp, and IP.</p>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4]">
              <h4 className="font-medium text-[#0B1020] mb-1">Brute Force Protection</h4>
              <p>5 failed login attempts trigger a 15-minute lockout per IP/email combination.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "backup" && (
        <div className="stratum-card p-6 max-w-2xl">
          <h3 className="font-display text-lg font-semibold mb-4">Backup & Export</h3>
          <div className="p-4 rounded-lg bg-[#065F46]/5 border border-[#065F46]/20 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-[#065F46]" />
              <span className="font-medium text-[#065F46]">Database is backed up</span>
            </div>
            <p className="text-sm text-[#6B7280]">MongoDB data is persisted and available for export.</p>
          </div>
          <p className="text-sm text-[#6B7280]">Full backup and restore functionality with point-in-time recovery is available in the production deployment. Contact your administrator for enterprise backup settings.</p>
        </div>
      )}
    </div>
  );
}
